/**
 * Onboarding tours — one per role.
 *
 * A tour points at things that are ALREADY on screen (header controls, sidebar entries) rather
 * than navigating page to page. The cloud shell changes its whole menu with the selected scope
 * (All Sites vs one site — see constants/navigation.ts), and pages behind an offline site are
 * blocked, so a tour that drove the router would break on exactly the installs a new user is
 * most likely to see. Instead every step is resolved against the live DOM when the tour starts
 * (see useOnboardingTour): a step whose target is missing — wrong scope, no permission, model
 * not active, small screen with the sidebar hidden — is simply left out.
 *
 * Bump a tour's `version` when its content changes meaningfully: everyone whose stored version
 * is older is offered the tour once more.
 */

export type TourId = 'admin' | 'operator' | 'viewer';

export interface TourStep {
  /** CSS selector. Omitted → a centred card with no highlight (intro / outro). */
  target?: string;
  title: string;
  description: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Only shown to users holding this permission. */
  permission?: string;
  /** Only shown to users holding at least one of these. */
  anyPermission?: string[];
  /**
   * Only shown while this holds for the page as it is right now — for pages whose layout
   * changes in place (Alert Board's Honeycomb / GIS / Map, Search vs Track), so "?" explains the
   * layout on screen rather than one the user has switched away from. Checked when the tour
   * starts, against the current URL.
   */
  when?: (at: TourLocation) => boolean;
}

/** Where the user is when a tour starts — what a step's `when` decides on. */
export interface TourLocation {
  pathname: string;
  params: URLSearchParams;
}

export interface TourDefinition {
  id: TourId;
  version: number;
  label: string;
  steps: TourStep[];
}

/**
 * A sidebar entry by its label. NavTree titles every row with the entry's name (and an offline
 * site turns a link into an inert div with "<name> — <reason>"), so the title is the one stable
 * handle that works for links, group headers and disabled rows alike, without touching the
 * shared nav component.
 */
const nav = (name: string) =>
  `[data-tour="sidebar"] [title="${name}"], [data-tour="sidebar"] [title^="${name} — "]`;

// ─── Steps shared between tours ──────────────────────────────────

const siteSwitcher: TourStep = {
  target: '[data-tour="site-switcher"]',
  title: 'Choose your scope',
  description:
    'Switch between All Sites and a single site here. Everything else — the menu, the alerts, the pages — follows what you pick. Star a site to open on it every time you log in.',
  side: 'bottom',
};

const sidebar: TourStep = {
  target: '[data-tour="sidebar"]',
  title: 'Main menu',
  description:
    'Your pages live here. The menu changes with the scope you picked above, and only shows what your role is allowed to use.',
  side: 'right',
};

const dashboard: TourStep = {
  target: nav('Dashboard'),
  title: 'Dashboard',
  description: 'The overview: site status, cameras online and today’s activity at a glance.',
  side: 'right',
};

const liveWall: TourStep = {
  target: nav('Live Wall'),
  title: 'Live Wall',
  description: 'Watch many cameras at once in a grid. Save layouts and switch between them.',
  side: 'right',
  anyPermission: ['live-wall.view', 'multi-site.view'],
};

const alertBoard: TourStep = {
  target: nav('Alert Board'),
  title: 'Alert Board',
  description: 'Incoming AI alerts in real time. Open one to see the snapshot and live view, then acknowledge it.',
  side: 'right',
  anyPermission: ['alerts.view', 'map.view'],
};

const recordings: TourStep = {
  target: nav('Recordings'),
  title: 'Recordings',
  description: 'Find and play back recorded footage by camera and time.',
  side: 'right',
  permission: 'recordings.view',
};

const aiAlerts: TourStep = {
  target: '[data-tour="ai-alerts"]',
  title: 'AI alert counter',
  description: 'Unhandled AI alerts on the selected site, by severity. Click it to open the alert list.',
  side: 'bottom',
  permission: 'alerts.view',
};

const helpButton: TourStep = {
  target: '[data-tour="tour-help"]',
  title: 'Help & tours',
  description:
    'Replay a tour any time — of the whole app, or just the page you’re on. Each page also offers a short tour the first time you open it; switch those tips off here if you don’t need them.',
  side: 'bottom',
};

const userMenu: TourStep = {
  target: '[data-tour="user-menu"]',
  title: 'Your account',
  description: 'Account and platform pages, the selected site’s settings, and logout.',
  side: 'bottom',
};

const welcome = (text: string): TourStep => ({
  title: 'Welcome to Trinetra',
  description: text,
});

const done: TourStep = {
  title: 'You’re all set',
  description: 'That’s the tour. Pick a site and start exploring — each page will offer its own short tour the first time you open it.',
};

// ─── Tours ───────────────────────────────────────────────────────

export const TOURS: Record<TourId, TourDefinition> = {
  admin: {
    id: 'admin',
    version: 1,
    label: 'Administrator tour',
    steps: [
      welcome('A quick look at where everything is: monitoring, alerts, cameras, AI models and platform administration.'),
      siteSwitcher,
      sidebar,
      dashboard,
      liveWall,
      alertBoard,
      {
        target: nav('Alert Settings'),
        title: 'Alert Settings',
        description: 'What raises an alert on each site and on the cloud, and who is told — a site\'s alerts reach that site\'s people only.',
        side: 'right',
        permission: 'alerts.config',
      },
      {
        target: nav('Cameras'),
        title: 'Cameras',
        description: 'Add, discover and group cameras, and control who can see them.',
        side: 'right',
        permission: 'cameras.view',
      },
      {
        target: nav('Alerts'),
        title: 'Alerts and rules',
        description: 'Browse past alerts and set the rules that decide what becomes an alert.',
        side: 'right',
        anyPermission: ['alerts.view', 'alerts.config'],
      },
      {
        target: nav('AI Models'),
        title: 'AI Models',
        description: 'See the models installed on the site and assign them to cameras.',
        side: 'right',
        permission: 'ai-models.view',
      },
      {
        target: nav('System Health'),
        title: 'System Health',
        description: 'CPU, GPU, storage and service status for the selected site.',
        side: 'right',
        permission: 'monitoring.view',
      },
      aiAlerts,
      {
        target: '[data-tour="system-alerts"]',
        title: 'System alert counter',
        description: 'Service and infrastructure problems across all sites — crashed services, unhealthy nodes.',
        side: 'bottom',
        permission: 'system_alert.view',
      },
      {
        target: '[data-tour="theme-toggle"]',
        title: 'Light or dark',
        description: 'Switch the theme to suit the control room.',
        side: 'bottom',
      },
      helpButton,
      {
        ...userMenu,
        description: 'Users and roles, platform health, the TURN server, the selected site’s settings, and logout.',
      },
      done,
    ],
  },

  operator: {
    id: 'operator',
    version: 1,
    label: 'Operator tour',
    steps: [
      welcome('A quick look at the pages you’ll use every shift: live video, alerts, recordings and search.'),
      siteSwitcher,
      sidebar,
      dashboard,
      liveWall,
      alertBoard,
      recordings,
      {
        target: nav('Alerts'),
        title: 'Alert list',
        description: 'Every alert with filters — review what happened and who handled it.',
        side: 'right',
        permission: 'alerts.view',
      },
      {
        target: nav('Search & Track'),
        title: 'Search & Track',
        description: 'Find a person or vehicle across cameras by appearance, and follow where they went.',
        side: 'right',
        permission: 'ai_events.view',
      },
      {
        target: nav('Watch List'),
        title: 'Watch List',
        description: 'People and vehicles you want to be told about the moment they appear.',
        side: 'right',
        permission: 'ai_events.view',
      },
      aiAlerts,
      helpButton,
      userMenu,
      done,
    ],
  },

  viewer: {
    id: 'viewer',
    version: 1,
    label: 'Viewer tour',
    steps: [
      welcome('A quick look at where to watch live video, alerts and recordings.'),
      siteSwitcher,
      dashboard,
      liveWall,
      alertBoard,
      recordings,
      helpButton,
      userMenu,
      done,
    ],
  },
};

/**
 * Which tour a user gets. The seeded system roles map directly; a custom role is placed by
 * what it can do, so a role named "Shift Lead" with config rights still gets the admin tour.
 */
export function resolveTourId(
  role: string | undefined,
  hasPermission: (permission: string) => boolean,
): TourId {
  if (role === 'super_admin' || role === 'admin') return 'admin';
  if (role === 'operator') return 'operator';
  if (role === 'viewer') return 'viewer';

  if (hasPermission('users.edit') || hasPermission('alerts.config') || hasPermission('cameras.config')) {
    return 'admin';
  }
  if (hasPermission('alerts.view') || hasPermission('ai_events.view')) return 'operator';
  return 'viewer';
}
